import { useEffect, useState } from 'react';
import { cognitoAuth } from '@/services/auth/cognito';
import { useAuthStore } from '@/store/authStore';
import { usersService } from '@/services/api/users';

export function useAuth() {
  const { user, setUser, setToken, setLoading, isAuthenticated, isLoading } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const isAuth = await cognitoAuth.checkAuth();

        if (isAuth) {
          const userInfo = await cognitoAuth.getUserInfo();
          const token = await cognitoAuth.getIdToken();

          if (userInfo && token) {
            // Set token first so API calls work
            setToken(token);

            // Try to fetch fresh user data from API
            try {
              const userData = await usersService.getCurrentUser();
              if (userData && userData.id) {
                setUser({
                  id: userData.id,
                  cognitoUserId: userData.cognitoUserId || userInfo.userId,
                  tenantId: userData.tenantId || userInfo.tenantId || '',
                  email: userData.email,
                  firstName: userData.firstName || userInfo.givenName || null,
                  lastName: userData.lastName || userInfo.familyName || null,
                  role: userData.role || (userInfo.role as any) || 'user',
                  status: userData.status || 'active',
                  permissions: userData.permissions || {},
                  lastLoginAt: userData.lastLoginAt || new Date().toISOString(),
                  createdAt: userData.createdAt || '',
                  updatedAt: userData.updatedAt || '',
                  createdBy: null,
                });
              } else {
                // Fallback to Cognito data if API fails
                setUser({
                  id: userInfo.userId,
                  cognitoUserId: userInfo.userId,
                  tenantId: userInfo.tenantId || '',
                  email: userInfo.email,
                  firstName: userInfo.givenName || null,
                  lastName: userInfo.familyName || null,
                  role: (userInfo.role as any) || 'user',
                  status: 'active',
                  permissions: {},
                  lastLoginAt: new Date().toISOString(),
                  createdAt: '',
                  updatedAt: '',
                  createdBy: null,
                });
              }
            } catch (apiError) {
              console.warn('Failed to fetch user from API, using Cognito data:', apiError);
              // Fallback to Cognito data if API fails
              setUser({
                id: userInfo.userId,
                cognitoUserId: userInfo.userId,
                tenantId: userInfo.tenantId || '',
                email: userInfo.email,
                firstName: userInfo.givenName || null,
                lastName: userInfo.familyName || null,
                role: (userInfo.role as any) || 'user',
                status: 'active',
                permissions: {},
                lastLoginAt: new Date().toISOString(),
                createdAt: '',
                updatedAt: '',
                createdBy: null,
              });
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initAuth();
  }, [setUser, setToken, setLoading]);

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || !isInitialized,
  };
}
